import { useState } from "react";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { CheckCircle2, Loader2, LogIn, MessageCircle, ShieldAlert, UserRoundCheck } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { InviteLinkShell } from "@/components/teams/InviteLinkShell";
import { useAuth } from "@/context/AuthContext";
import { ApiError } from "@/lib/api";
import type { InvitationTokenLookup } from "@/lib/api/teams";
import {
  useAcceptInvitation,
  useInvitationTokenLookup,
} from "@/lib/api/teams.queries";
import { useActiveTeam } from "@/lib/team-context";
import { maskColombianPhone } from "@/lib/phone";

/**
 * Landing page for the WhatsApp "Aceptar" URL button (PF-345). Public by
 * design: it resolves the single-use token to the invitation context, walks an
 * unauthenticated invitee through sign-in (returning here afterwards), then
 * accepts with one tap. Membership stays bound to the invited phone — the
 * BFF rejects a mismatched session with INVITATION_PHONE_MISMATCH.
 */
export const Route = createFileRoute("/invite/accept/$token")({
  component: InviteAcceptPage,
});

function InviteAcceptPage() {
  const { token } = Route.useParams();
  return (
    <InviteLinkShell>
      <InviteAcceptContent token={token} />
    </InviteLinkShell>
  );
}

function InviteAcceptContent({ token }: { token: string }) {
  const { t } = useTranslation();
  const lookup = useInvitationTokenLookup(token);

  if (lookup.isPending) {
    return (
      <div className="flex items-center justify-center gap-2 py-16 text-sm text-muted-foreground">
        <Loader2 className="h-5 w-5 animate-spin text-primary" />
        {t("invite_link.loading")}
      </div>
    );
  }

  if (lookup.isError || !lookup.data || lookup.data.kind !== "accept") {
    return (
      <StateCard
        title={t("invite_link.not_found.title")}
        body={t("invite_link.not_found.body")}
      />
    );
  }

  const invitation = lookup.data;
  switch (invitation.status) {
    case "expired":
      return (
        <StateCard
          title={t("invite_link.expired.title")}
          body={t("invite_link.expired.body")}
        />
      );
    case "cancelled":
    case "declined":
      return (
        <StateCard
          title={t("invite_link.cancelled.title")}
          body={t("invite_link.cancelled.body")}
        />
      );
    case "accepted":
      return (
        <StateCard
          title={t("invite_link.accepted.title")}
          body={t("invite_link.accepted.body")}
        />
      );
    default:
      return <PendingAccept token={token} invitation={invitation} />;
  }
}

function PendingAccept({
  token,
  invitation,
}: {
  token: string;
  invitation: InvitationTokenLookup;
}) {
  const { t } = useTranslation();
  const auth = useAuth();
  const navigate = useNavigate();
  const { setActiveTeamId } = useActiveTeam();
  const accept = useAcceptInvitation();
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [signingIn, setSigningIn] = useState(false);
  const [joinedTeam, setJoinedTeam] = useState<string | null>(null);

  const summary = invitation.inviterName
    ? t("invite_link.summary", {
        inviter: invitation.inviterName,
        team: invitation.teamName,
        role: t(`teams.role.${invitation.role}`, invitation.role),
      })
    : t("invite_link.summary_no_inviter", {
        team: invitation.teamName,
        role: t(`teams.role.${invitation.role}`, invitation.role),
      });

  if (joinedTeam) {
    return (
      <Card>
        <CardContent className="flex flex-col items-center gap-3 py-10 text-center">
          <CheckCircle2 className="h-10 w-10 text-primary" />
          <p className="text-lg font-semibold">
            {t("invite_link.accept.success.title", { team: joinedTeam })}
          </p>
          <p className="text-sm text-muted-foreground">
            {t("invite_link.accept.success.body")}
          </p>
        </CardContent>
      </Card>
    );
  }

  const onSignIn = async () => {
    setSigningIn(true);
    try {
      await auth.signIn(`/invite/accept/${token}`);
    } catch {
      setSigningIn(false);
      setErrorMessage(t("invite_link.accept.error.generic"));
    }
  };

  const onAccept = () => {
    setErrorMessage(null);
    accept.mutate(
      { token },
      {
        onSuccess: (joined) => {
          setActiveTeamId(joined.teamId);
          setJoinedTeam(joined.teamName);
          window.setTimeout(() => {
            void navigate({
              to: "/teams/$teamId",
              params: { teamId: joined.teamId },
            });
          }, 1200);
        },
        onError: (err) => {
          if (err instanceof ApiError && err.code === "INVITATION_PHONE_MISMATCH") {
            const details = err.details as { invitedPhoneMasked?: string } | undefined;
            setErrorMessage(
              t("invite_link.accept.error.phone_mismatch", {
                phone: details?.invitedPhoneMasked ?? invitation.invitedPhoneMasked,
              }),
            );
            return;
          }
          setErrorMessage(
            err instanceof ApiError
              ? err.message
              : t("invite_link.accept.error.generic"),
          );
        },
      },
    );
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-lg">
          <UserRoundCheck className="h-5 w-5 text-primary" />
          {t("invite_link.title")}
        </CardTitle>
      </CardHeader>
      <CardContent className="flex flex-col gap-4">
        <p className="text-sm">{summary}</p>
        <Alert variant="info">
          <MessageCircle className="h-4 w-4" />
          <AlertTitle>{t("invite_link.for_phone_title")}</AlertTitle>
          <AlertDescription>
            {t("invite_link.for_phone", { phone: invitation.invitedPhoneMasked })}
          </AlertDescription>
        </Alert>

        {errorMessage ? (
          <div className="flex items-start gap-2 rounded-md border border-destructive/30 bg-destructive/10 p-3 text-sm text-destructive">
            <ShieldAlert className="mt-0.5 h-4 w-4 flex-none" />
            <span>{errorMessage}</span>
          </div>
        ) : null}

        {auth.status === "authenticated" ? (
          <Button size="lg" onClick={onAccept} disabled={accept.isPending}>
            {accept.isPending ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <CheckCircle2 className="h-4 w-4" />
            )}
            {t("invite_link.accept.cta")}
          </Button>
        ) : (
          <>
            <p className="text-xs text-muted-foreground">
              {t("invite_link.accept.signin_hint")}
            </p>
            <Button
              size="lg"
              onClick={() => void onSignIn()}
              disabled={signingIn || auth.status === "loading"}
            >
              {signingIn ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <LogIn className="h-4 w-4" />
              )}
              {t("invite_link.accept.signin_cta")}
            </Button>
          </>
        )}

        {auth.status === "authenticated" ? (
          <p className="text-center text-xs text-muted-foreground">
            {t("invite_link.accept.session_as", {
              phone: maskColombianPhone(auth.user.phone),
            })}
          </p>
        ) : null}
      </CardContent>
    </Card>
  );
}

function StateCard({ title, body }: { title: string; body: string }) {
  const { t } = useTranslation();
  return (
    <Card>
      <CardContent className="flex flex-col items-center gap-3 py-10 text-center">
        <ShieldAlert className="h-10 w-10 text-muted-foreground" />
        <p className="text-lg font-semibold">{title}</p>
        <p className="text-sm text-muted-foreground">{body}</p>
        <p className="text-xs text-muted-foreground">{t("invite_link.footer_hint")}</p>
      </CardContent>
    </Card>
  );
}
